import { Fragment, useContext, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { Tooltip, Drawer, Theme, useTheme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridValueFormatterParams,
  GridValueGetterParams
} from '@mui/x-data-grid';

import type { Country, EmissionFactor } from '@esg/esg-global-types';
import type {
  CreateEmissionFactorClientData,
  SoftDeleteEmissionFactorClientData,
  UpdateEmissionFactorClientData
} from '@esg/syntiro-api-core';
import { MetadataError } from '@ep/error-handling';

import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import ConfigAddWidget from '../ConfigAddWidget';
import type { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget from '../ConfigEditWidget';
import type { EditInput } from '../ConfigEditWidget';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { allowDeleteEmissionFactor, emissionFactorLabel } from '../../../lib/app/emission_factor';
import {
  createEmissionFactor,
  getEmissionFactors,
  replicateMasterListEmissionFactorsToCompany,
  softDeleteEmissionFactor,
  updateEmissionFactor
} from '../../../graphql/services/emission_factor.service';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import type { MetricExtended } from '../../../lib/metric_capture/metric';
import MetricSelect from '../../shared/input/select/MetricSelect';
import CountrySelect from '../../shared/input/select/CountrySelect';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

interface TabProps {
  master_list?: boolean;
}

const SOURCE = 'PanelEmissionFactors.tsx';

export default function PanelEmissionFactors(props: TabProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { user } = useContext(UserContext);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState(false);
  const [isCreateWidget, setIsCreateWidget] = useState(true);
  const [gridLoading, setGridLoading] = useState(true);
  const [emissionFactorRows, setEmissionFactorRows] = useState<EmissionFactor[]>([]);
  const [actionRow, setActionRow] = useState<EmissionFactor | null>(null);
  const [allowEmissionFactorDelete, setAllowEmissionFactorDelete] = useState<boolean>(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const theme: Theme = useTheme();
  const primaryBlue: string = theme.palette.primary.main;

  const handleCreateClick = (): void => {
    setActionRow(null);
    setDisplayWidgetPanelRight(true);
    setIsCreateWidget(true);
  };

  const handleEditClick = (row: EmissionFactor): void => {
    setActionRow(row);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleDeleteClick = async (row: EmissionFactor): Promise<void> => {
    setActionRow(row);
    if (!props.master_list && group && company) {
      setAllowEmissionFactorDelete(await allowDeleteEmissionFactor(row, group.id, company.id));
    } else {
      setAllowEmissionFactorDelete(true);
    }
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
  };

  const handleReplicateMasterListEmissionFactorsToCompany = async (): Promise<void> => {
    try {
      setIsSyncing(true);
      const syncedEmissionFactors: string[] | null =
        await replicateMasterListEmissionFactorsToCompany({
          group_id: group?.id,
          company_id: company?.id,
          audit_user_email: user?.email
        });
      if (syncedEmissionFactors) {
        setEmissionFactorRows([]);
        fetchRows();
        setFeedbackData({
          message: 'Successfully synced emission factors',
          state: true,
          type: 'info'
        });
      }
    } catch (error: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          error instanceof Error
            ? error.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'replicateMasterListEmissionFactorsToCompany'),
          {
            user,
            group,
            company
          },
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Could not sync emission factors', trackingId),
        state: true,
        type: 'error'
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchRows = async (): Promise<void> => {
    try {
      setGridLoading(true);
      const emissionFactors: EmissionFactor[] = await getEmissionFactors({
        group_id: group?.id,
        company_id: company?.id,
        master_list: props.master_list
      });
      emissionFactors.map((emissionFactor: EmissionFactor) => {
        emissionFactor.target_year = moment(emissionFactor.start_date).format('yyyy');
        emissionFactor.category =
          emissionFactor.metric_group && 'name' in emissionFactor.metric_group
            ? emissionFactor.metric_group.name
            : '';
        emissionFactor.start_date = moment(emissionFactor.start_date).format('DD MMM yyyy');
        emissionFactor.end_date = moment(emissionFactor.end_date).format('DD MMM yyyy');
        return {
          ...emissionFactor
        };
      });
      setEmissionFactorRows(emissionFactors);
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          !props.master_list
            ? {
                group,
                company
              }
            : {},
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch emission factors', trackingId),
        state: true,
        type: 'error'
      });
    } finally {
      setGridLoading(false);
    }
  };

  const deleteRow = async (row: EmissionFactor): Promise<void> => {
    try {
      const softDeleteEmissionFactorClientData: SoftDeleteEmissionFactorClientData = {
        id: row.id,
        metric_group_id: row.metric_group?.id ?? '',
        metric_id: row.metric?.id ?? ''
      };
      await softDeleteEmissionFactor(
        { group_id: group?.id, company_id: company?.id, master_list: props.master_list },
        softDeleteEmissionFactorClientData
      );
      setEmissionFactorRows(
        emissionFactorRows.filter((obj) => {
          return obj.id !== row.id;
        })
      );
      setShowDeleteModal(false);
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          !props.master_list
            ? {
                group,
                company,
                row
              }
            : {},
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to remove emission factor', trackingId),
        state: true,
        type: 'error'
      });
    }
  };

  const createRow = async (
    metric: MetricExtended,
    startDate: Moment,
    endDate: Moment,
    factor: number,
    source: string,
    country: Country | null = null
  ): Promise<void> => {
    try {
      const createEmissionFactorClientData: CreateEmissionFactorClientData = {
        metric_group_id: metric.metric_group.id,
        metric_id: metric.id,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        factor: parseFloat(String(factor)),
        source,
        country: country ? { id: country.id } : null
      };
      const createdEmissionFactor = await createEmissionFactor(
        { group_id: group?.id, company_id: company?.id, master_list: props.master_list },
        createEmissionFactorClientData,
        { return_data: true, create_audit_log: true }
      );
      if (createdEmissionFactor) {
        setEmissionFactorRows([
          {
            ...createdEmissionFactor,
            metric,
            target_year: moment(startDate).format('yyyy'),
            category: metric.metric_group.name,
            start_date: moment(startDate).format('DD MMM yyyy'),
            end_date: moment(endDate).format('DD MMM yyyy')
          },
          ...emissionFactorRows
        ]);
      } else {
        throw new MetadataError('Could not create Emission Factor in database', {});
      }
      setDisplayWidgetPanelRight(false);
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRow'),
          !props.master_list
            ? {
                group,
                company,
                metric,
                start_date: startDate,
                end_date: endDate,
                factor,
                source
              }
            : {
                metric,
                start_date: startDate,
                end_date: endDate,
                factor,
                source
              },
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to create emission factor', trackingId),
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (
    newRowData: EmissionFactor,
    oldRowData: EmissionFactor
  ): Promise<void> => {
    try {
      const updateEmissionFactorClientData: UpdateEmissionFactorClientData = {
        id: newRowData.id,
        metric_group_id: newRowData.metric_group?.id ?? '',
        metric_id: newRowData.metric?.id ?? '',
        start_date: newRowData.start_date,
        end_date: newRowData.end_date,
        factor: parseFloat(String(newRowData.factor)),
        source: newRowData.source,
        country: newRowData.country ? { id: newRowData.country.id } : null
      };
      await updateEmissionFactor(
        { group_id: group?.id, company_id: company?.id, master_list: props.master_list },
        updateEmissionFactorClientData,
        { return_data: false, create_audit_log: true }
      );
      setEmissionFactorRows(
        emissionFactorRows.map((emissionFactor) => {
          if (emissionFactor.id === newRowData.id) {
            newRowData.target_year = moment(newRowData.start_date).format('yyyy');
            newRowData.category =
              newRowData.metric_group && 'name' in newRowData.metric_group
                ? newRowData.metric_group.name
                : '';
            newRowData.start_date = moment(newRowData.start_date).format('DD MMM yyyy');
            newRowData.end_date = moment(newRowData.end_date).format('DD MMM yyyy');
            return {
              ...newRowData
            };
          } else {
            return emissionFactor;
          }
        })
      );
      setDisplayWidgetPanelRight(false);
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          !props.master_list
            ? {
                group,
                company,
                new_row_data: newRowData,
                old_row_data: oldRowData
              }
            : {
                new_row_data: newRowData,
                old_row_data: oldRowData
              },
          trackingId
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to update emission factor', trackingId),
        state: true,
        type: 'error'
      });
    }
  };

  useEffect(
    () => {
      try {
        setEmissionFactorRows([]);
        (async () => {
          fetchRows();
        })().catch((error) => {
          throw new Error(error);
        });
      } catch (err: unknown) {
        const trackingId: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
            null,
            trackingId
          )
        );
        setFeedbackData({
          message: FEEDBACK_ACTION_FAILURE('An error occurred', trackingId),
          state: true,
          type: 'error'
        });
      }
      return;
    },
    props.master_list ? [] : [group, company]
  );

  const columns: GridColDef[] = [
    {
      field: 'category',
      headerName: 'Category',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'start_date',
      headerName: 'Start',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'end_date',
      headerName: 'End',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'target_year',
      headerName: 'Target Year',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'metric',
      headerName: 'Metric',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metric?.name ?? '';
      }
    },
    {
      field: 'factor',
      headerName: 'Factor',
      headerAlign: 'left',
      align: 'left',
      type: 'number',
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        if (params.value == null) return 0;
        return params.value.toLocaleString(undefined, {
          maximumFractionDigits: 10,
          useGrouping: false
        });
      },
      flex: 1
    },
    {
      field: 'metric_unit',
      headerName: 'Unit',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metric.unit;
      }
    },
    {
      field: 'source',
      headerName: 'Source',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'country',
      headerName: 'Country',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        const country = params.row.country;
        if (!country) return 'Global';
        return 'name' in country ? country.name : country.id;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit emission factor">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete emission factor">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const countrySelectInputFactory = (
    handleInputChange: (inputId: string, value: unknown) => void
  ) => (
    <CountrySelect
      handleChangeCountries={(value) => handleInputChange('country', value)}
      master_list={props.master_list}
      group_id={group?.id}
      company_id={company?.id}
      selected_options={(actionRow?.country as Country) ?? null}
    />
  );

  const metricSelectInputFactory = (
    handleInputChange: (inputId: string, value: unknown) => void
  ) => {
    return (
      <MetricSelect
        handleChangeMetrics={(value) => handleInputChange('metric', value)}
        selected_options={(actionRow?.metric as MetricExtended) ?? null}
        master_list={props.master_list}
        group_id={group?.id}
        company_id={company?.id}
      />
    );
  };

  const createWidgetInputs: readonly CreateInput[] = [
    {
      id: 'metric',
      type: 'node',
      label: 'Metrics',
      inputNodeFactory: metricSelectInputFactory
    },
    {
      id: 'start_date',
      type: 'month_year',
      label: 'Start',
      defaultValue: moment().startOf('month')
    },
    {
      id: 'end_date',
      type: 'month_year',
      label: 'End',
      defaultValue: moment().endOf('month')
    },
    {
      id: 'factor',
      type: 'number',
      label: 'Factor'
    },
    {
      id: 'source',
      type: 'text',
      label: 'Source'
    },
    {
      id: 'country',
      type: 'node',
      label: 'Country',
      optional: true,
      inputNodeFactory: countrySelectInputFactory
    }
  ];

  const editWidgetInputs: EditInput[] = [
    {
      id: 'start_date',
      type: 'month_year',
      label: 'Start',
      defaultValue: moment(actionRow?.start_date)
    },
    {
      id: 'end_date',
      type: 'month_year',
      label: 'End',
      defaultValue: moment(actionRow?.end_date)
    },
    {
      id: 'factor',
      type: 'number',
      label: 'Factor'
    },
    {
      id: 'source',
      type: 'text',
      label: 'Source'
    },
    {
      id: 'country',
      type: 'node',
      label: 'Country',
      optional: true,
      inputNodeFactory: countrySelectInputFactory
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={showDeleteModal}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={
          actionRow?.metric && 'name' in actionRow.metric ? actionRow.metric.name : undefined
        }
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
        allow_delete={allowEmissionFactorDelete}
        item_type={'Emission Factor'}
        delete_warning={'You will be required to create this Emission Factor again if deleted.'}
      />

      {/* Side Widget Panel */}
      <Fragment key={'right_add'}>
        <Drawer
          anchor={'right'}
          open={displayWidgetPanelRight}
          onClose={() => {
            setDisplayWidgetPanelRight(false);
          }}
          PaperProps={{ style: { width: '40%', padding: '1.5rem' } }}
        >
          {/* Create / Edit Widget */}
          {isCreateWidget ? (
            <ConfigAddWidget
              widget_label={emissionFactorLabel.one}
              icon={<CreateIcon height={40} fill={primaryBlue} stroke={primaryBlue} />}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              inputs={createWidgetInputs}
              createFunction={(
                metric: MetricExtended,
                startDate: Moment,
                endDate: Moment,
                factor: number,
                source: string,
                country: Country | null
              ) => createRow(metric, startDate, endDate, factor, source, country)}
            />
          ) : (
            <ConfigEditWidget
              widget_label={emissionFactorLabel.one}
              icon={<EditIcon width={40} fill={primaryBlue} />}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              edit_entity={actionRow}
              inputs={editWidgetInputs}
              handleEditInput={(inputId: string, value: unknown) =>
                actionRow && setActionRow({ ...actionRow, [inputId]: value })
              }
              confirmEditFunction={(editEntity: EmissionFactor, originalData: EmissionFactor) =>
                updateRow(editEntity, originalData)
              }
            />
          )}
        </Drawer>
      </Fragment>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'target_year', sort: 'desc' }]
          },
          columns: {
            columnVisibilityModel: {
              start_date: false,
              end_date: false
            }
          }
        }}
        hideFooter={emissionFactorRows.length > 1 ? false : true}
        columnHeaderHeight={emissionFactorRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={emissionFactorRows}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <>
              {props.master_list ? (
                <DataGridToolbar
                  entity_label={emissionFactorLabel}
                  disable_export={emissionFactorRows.length === 0}
                  handleCreate={handleCreateClick}
                />
              ) : (
                <DataGridToolbar
                  entity_label={emissionFactorLabel}
                  disable_export={emissionFactorRows.length === 0}
                  handleCreate={handleCreateClick}
                  handleSync={handleReplicateMasterListEmissionFactorsToCompany}
                  disable_sync={isSyncing}
                  is_syncing={isSyncing}
                />
              )}
            </>
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={emissionFactorLabel} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' } }}
      />
    </>
  );
}
